import { motion } from "framer-motion";
import { Palette, Megaphone, Magnet, Filter, Bot, Rocket } from "lucide-react";

const stages = [
  { name: "Brand", desc: "Identity, narrative, design language.", color: "from-neon-orange to-neon-magenta", icon: Palette },
  { name: "Demand", desc: "Multi-channel attention engines.", color: "from-neon-magenta to-neon-purple", icon: Megaphone },
  { name: "Leads", desc: "Capture systems & qualification.", color: "from-neon-purple to-neon-blue", icon: Magnet },
  { name: "Conversion", desc: "Funnels, CRO, sales enablement.", color: "from-neon-blue to-neon-cyan", icon: Filter },
  { name: "Automation", desc: "AI agents & workflow systems.", color: "from-neon-cyan to-emerald-400", icon: Bot },
  { name: "Scale", desc: "Compounding growth machinery.", color: "from-emerald-400 to-neon-orange", icon: Rocket },
];

export default function Engine() {
  return (
    <section id="engine" data-testid="engine-section" className="section relative">
      <div className="container-x">
        <div className="max-w-3xl mb-16">
          <p className="overline mb-4">The Novarise Engine</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            A six-stage system that compounds <span className="text-gradient-neon">growth</span>.
          </h2>
          <p className="mt-5 text-white/60 max-w-xl">
            Every project flows through the same operating model — designed to turn raw
            attention into predictable, compounding revenue.
          </p>
        </div>

        <div className="relative">
          {/* Animated connector */}
          <div className="absolute left-0 right-0 top-[58px] h-px hidden lg:block">
            <div className="h-px w-full flow-line opacity-60" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 lg:gap-4">
            {stages.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.55, delay: i * 0.08 }}
                  className="relative"
                  data-testid={`engine-stage-${s.name.toLowerCase()}`}
                >
                  <div className="glass rounded-2xl p-5 h-full hover:border-white/20 transition-colors group">
                    <div className={`relative w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-[0_8px_28px_-10px_rgba(0,0,0,0.7)]`}>
                      <Icon size={20} className="text-white" />
                      <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-300 bg-gradient-to-br from-white/10 to-transparent" />
                    </div>
                    <div className="mt-4 flex items-baseline gap-2">
                      <span className="font-mono text-[11px] text-white/40">0{i + 1}</span>
                      <h3 className="text-xl font-bold tracking-tight">{s.name}</h3>
                    </div>
                    <p className="mt-2 text-sm text-white/55 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
